import { type PayableRuleSchemaWithStats } from '@spendesk/bookkeeping-core-types';
import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useNotifications, NotificationType } from 'modules/app/notifications';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { useParams } from 'src/core/common/hooks/useParams';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routes } from 'src/core/constants/routes';
import {
  type EnrichedPayableRule,
  useEnrichedPayableAutomationRulesQuery,
} from 'src/core/modules/payable';

import { PayableRuleCreateWrapper } from './PayableRuleCreate';
import { PayableRulesFilters } from './PayableRulesFilters';
import { PayableRulesTable } from './PayableRulesTable';
import { PayableRuleDetails } from './components/PayableRuleDetails';
import { PayableRuleEmptyState } from './components/PayableRuleEmptyState';
import { PayableRulesTourModal } from './components/PayableRulesTourModal';
import { useGetPayableRules } from '../../hooks/useGetPayableRules';
import {
  defaultMCRLimit,
  type MCRLimit,
  useGetPayableRulesMCRLimit,
} from '../../hooks/useGetPayableRulesMCRLimit';

export const PayableRulesSetupPageContainer = () => {
  const mcrLimitQueryState = useGetPayableRulesMCRLimit();
  const queryState = useGetPayableRules(true);
  const { pushNotif } = useNotifications();
  const { t } = useTranslation('global');
  const { payableRuleId } = useParams(
    routes.COMPANY_ACCOUNTING_PAYABLE_RULES_SETUP.path,
  );

  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const isModalOpen = params.get('tourModal') === 'true';

  const openModal = () => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tourModal', 'true');
    history.replace({ search: newParams.toString() });
  };

  const closeModal = () => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete('tourModal');
    history.replace({ search: newParams.toString() });
  };

  // Ref to track if the error notification has already been shown
  const hasShownErrorNotif = useRef(false);

  const payableRules: PayableRuleSchemaWithStats[] =
    queryState.status === 'success' && Array.isArray(queryState?.data)
      ? queryState.data
      : [];

  const mcrLimit: MCRLimit = unwrapQuery(mcrLimitQueryState) ?? defaultMCRLimit;

  useEffect(() => {
    if (queryState.status === 'error' && !hasShownErrorNotif.current) {
      pushNotif({
        message: t('errors:somethingWrong'),
        type: NotificationType.Danger,
      });
      hasShownErrorNotif.current = true;
    }
  }, [queryState.status === 'error' && !hasShownErrorNotif.current]);

  const enrichedPayableRulesQuery =
    useEnrichedPayableAutomationRulesQuery(payableRules);
  const enrichedPayableRules:
    | EnrichedPayableRule<PayableRuleSchemaWithStats>[]
    | undefined = unwrapQuery(enrichedPayableRulesQuery);

  if (enrichedPayableRulesQuery.status !== 'success' || !enrichedPayableRules) {
    return null;
  }

  const selectedPayableRule = (enrichedPayableRules ?? []).find(
    (rule) => rule.id === payableRuleId,
  );

  return (
    <div className="w-full">
      <PayableRulesTourModal isOpen={isModalOpen} closeModal={closeModal} />
      {enrichedPayableRules.length > 0 ? (
        <>
          <PayableRulesFilters openModal={openModal} />
          <PayableRulesTable
            enrichedPayableRules={enrichedPayableRules}
            selectedPayableRuleId={selectedPayableRule?.id}
          />
        </>
      ) : (
        <PayableRuleEmptyState openModal={openModal} />
      )}
      {payableRuleId && payableRuleId === 'new' && (
        <PayableRuleCreateWrapper mcrLimit={mcrLimit} />
      )}
      {payableRuleId && payableRuleId !== 'new' && !!selectedPayableRule && (
        <PayableRuleDetails
          payableRule={selectedPayableRule}
          key={selectedPayableRule.id}
          mcrLimit={mcrLimit}
        />
      )}
    </div>
  );
};
